<template>
  <div class="header-search-container">
    <input
      :id="name"
      type="text"
      v-model="computedValue"
      class="search-input"
      :placeholder="placeholder"
      @focus="emit('input-focus')"
      @blur="emit('input-blur')"
      @keydown.enter="emit('submit')"
    />
    <UiIcon
      v-if="showFilterIcon"
      name="search"
      size="24px"
      class="filter-icon"
      color-fill="var(--state-color)"
    />
    <UiIcon
      v-if="showCloseIcon"
      name="cancel-filled"
      size="16px"
      class="close-icon"
      color-fill="var(--color-base-grey-30)"
      @click="close"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import type { WritableComputedRef } from 'vue'
import UiIcon from '@sephora-asia/ui-icon/UiIcon.vue'

interface Props {
  value: string | number
  name: string
  placeholder: string
}

interface Emits {
  (e: 'close'): void
  (e: 'input-blur'): void
  (e: 'input-focus'): void
  (e: 'input', value: string | number): void
  (e: 'submit'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const newValue = ref<string | number>(props.value)
const computedValue: WritableComputedRef<string | number> = computed({
  get(): string | number {
    return newValue.value
  },
  set(value: string | number): void {
    newValue.value = value
    emit('input', value)
  },
})

watch(
  () => props.value,
  (propsVal: string | number) => {
    newValue.value = propsVal
  }
)

const showFilterIcon = computed<boolean>(() => {
  if (newValue.value) {
    return false
  } else {
    return true
  }
})

const showCloseIcon = computed<boolean>(() => {
  return !!newValue.value
})

function close() {
  newValue.value = ''
  emit('input', '')
  emit('close')
}
</script>

<style lang="scss" scoped>
.header-search-container {
  --state-color: var(--color-base-grey-30);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  position: relative;

  input.search-input {
    width: 100%;
    height: 40px;
    padding: 0px 28px 0px 12px;
    border-radius: 99px;
    border: 1px solid var(--state-color);
    background: var(--color-base-white);

    &:focus {
      outline: none;
    }

    &:focus {
      --state-color: var(--color-base-black);

      & ~ .filter-icon :deep(svg) {
        fill: var(--color-base-black);
      }
    }

    @include media(mobile, tablet) {
      height: 32px;
    }
  }

  .filter-icon,
  .close-icon {
    position: absolute;
    right: 12px;
  }
}
</style>
